// src/components/Widgets/StudioSpaceWidget.jsx
import React, { useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { useStudioSpaces } from "@/contexts/StudioSpaceContext";
import { useNotifications } from "../../contexts/NotificationsContext"; // provider already in App.jsx
import WidgetHeader from "../Common/WidgetHeader";

// Lazy load SpaceBookingModal to avoid context provider issues
const SpaceBookingModal = lazy(() => import("../Modals/SpaceBookingModal"));

function fmtTimeHM(hm) {
  if (!hm) return "--:--";
  // hm like "14:30": format to locale time
  try {
    const [h, m] = hm.split(":").map((x) => parseInt(x, 10));
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return hm;
  }
}
function fmtDateStr(dateStr) {
  try {
    return new Date(dateStr).toLocaleDateString();
  } catch {
    return dateStr;
  }
}

export default function StudioSpaceWidget() {
  const { spaces, listBookingsForDay, cancelBooking } = useStudioSpaces();
  const [day, setDay] = useState(() => new Date().toISOString().slice(0, 10));
  const [open, setOpen] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState(null); // for editing specific space
  const [manageTab, setManageTab] = useState(false); // open modal directly on Manage
  const { notifyAndInbox } = useNotifications?.() || { notifyAndInbox: null };

  const dayBookings = useMemo(
    () => listBookingsForDay(day),
    [day, listBookingsForDay]
  );

  function handleCancel(id) {
    cancelBooking(id);
    notifyAndInbox?.({
      notification: {
        id: `studio-booking-cancel-${id}`,
        title: "Booking cancelled",
        message: "The studio has been notified.",
        level: "info",
        source: "bookings",
        world: "studio",
        entity: { type: "booking", id },
        action: {
          kind: "NAVIGATE",
          to: "/dashboard/workshops",
          params: { bookingId: id },
        },
      },
    });
  }

  return (
    <div className="h-full w-full rounded-lg border border-white/5 bg-slate-900/60 p-4 text-white shadow-lg">
      <WidgetHeader
        title="Studio Space Booking"
        right={
          <div className="flex gap-2">
            <button
              onClick={() => {
                setManageTab(true);
                setOpen(true);
              }}
              className="rounded-lg bg-slate-700/70 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-slate-600/80"
              title="Define spaces & quantities"
            >
              Manage
            </button>
            <button
              onClick={() => {
                setManageTab(false);
                setOpen(true);
              }}
              className="rounded-lg bg-amber-500/90 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-black transition hover:bg-amber-400"
              title="Create a new booking"
            >
              New
            </button>
          </div>
        }
      />

      <div className="text-[11px] uppercase tracking-[0.2em] text-slate-300">
        Your Studio Spaces
      </div>
      <p className="mt-1 text-sm text-slate-300">
        Click a space to manage it, or create a new one.
      </p>

      {/* Spaces Cards Grid */}
      {spaces.length > 0 && (
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          {spaces.map((space) => {
            const pricing = space.pricing || {};
            const capacity = space.capacity || {};
            
            return (
              <div
                key={space.id}
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-slate-800/60 p-4 transition hover:border-white/20 hover:bg-slate-800/80"
              >
                {/* Header with status */}
                <div className="mb-3 flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {/* Status indicator */}
                    <span
                      className={`h-2.5 w-2.5 rounded-full ${
                        space.active ? "bg-green-400" : "bg-slate-500"
                      }`}
                      title={space.active ? "Visible to visitors" : "Hidden from visitors"}
                    />
                    <span className="text-xs text-slate-400">
                      {space.active ? "Published" : "Draft"}
                    </span>
                  </div>
                  
                  {/* Type badge */}
                  {space.type && (
                    <span className="rounded-full bg-white/10 px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-slate-300">
                      {space.type}
                    </span>
                  )}
                </div>

                {/* Space name */}
                <h3 className="mb-1 text-lg font-semibold text-white">
                  {space.name || "Untitled Space"}
                </h3>

                {/* Location */}
                {space.location && (
                  <p className="mb-3 text-xs text-slate-400">
                    📍 {space.location}
                  </p>
                )}

                {/* Tags */}
                {space.tags && space.tags.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-1">
                    {space.tags.slice(0, 3).map((tag, idx) => (
                      <span
                        key={idx}
                        className="rounded-full bg-slate-700/60 px-2 py-0.5 text-[10px] text-slate-300"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Capacity and pricing */}
                <div className="mb-3 flex items-center gap-4 text-xs text-slate-400">
                  {capacity.seats && (
                    <div className="flex items-center gap-1">
                      <span>👥</span>
                      <span>Up to {capacity.seats} people</span>
                    </div>
                  )}
                </div>

                {/* Pricing */}
                {(pricing.hourly || pricing.daily) && (
                  <div className="mb-3 flex items-center gap-2 text-sm">
                    <span className="text-slate-400">$</span>
                    {pricing.hourly && (
                      <span className="font-semibold text-white">
                        ${pricing.hourly}
                        <span className="text-xs text-slate-400">/hour</span>
                      </span>
                    )}
                    {pricing.hourly && pricing.daily && (
                      <span className="text-slate-500">•</span>
                    )}
                    {pricing.daily && (
                      <span className="font-semibold text-white">
                        ${pricing.daily}
                        <span className="text-xs text-slate-400">/day</span>
                      </span>
                    )}
                  </div>
                )}

                {/* Action button */}
                <button
                  onClick={() => {
                    setSelectedSpace(space);
                    setManageTab(true);
                    setOpen(true);
                  }}
                  className="w-full rounded-lg bg-amber-500/90 px-3 py-2 text-sm font-semibold uppercase tracking-wide text-black transition hover:bg-amber-400"
                >
                  Manage Space
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {spaces.length === 0 && (
        <div className="mt-4 rounded-2xl border border-white/10 bg-slate-800/60 px-4 py-4 text-sm text-slate-200">
          <div className="font-semibold">No spaces yet</div>
          <p className="mt-1 text-xs text-slate-400">
            Click <span className="font-semibold text-white">New</span> to create your first studio space.
          </p>
        </div>
      )}

      {/* Booking summary */}
      <div className="mt-4 flex items-center gap-2 rounded-2xl border border-white/10 bg-slate-800/60 px-4 py-3 text-xs">
        <div className="flex items-center gap-2">
          <span className="text-slate-300">Day</span>
          <input
            type="date"
            value={day}
            onChange={(e) => setDay(e.target.value)}
            className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs text-white focus:border-amber-400/60 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
          />
        </div>
        <div className="ml-auto flex items-center gap-3 text-xs text-slate-300">
          <span>{spaces.length} spaces</span>
          <span className="h-4 w-px bg-white/10" />
          <span>{dayBookings.length} bookings</span>
        </div>
      </div>

      {/* Bookings List */}
      <div className="mt-4 space-y-3">
        {dayBookings.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-slate-800/60 px-4 py-4 text-sm text-slate-200">
            <div className="font-semibold">
              No bookings for {fmtDateStr(day)}.
            </div>
            <p className="mt-1 text-xs text-slate-400">
              Use <span className="font-semibold text-white">New</span> to create
              a booking, or <span className="font-semibold text-white">Manage</span> to
              configure wheels, benches and kilns.
            </p>
          </div>
        ) : (
          dayBookings.map((b) => (
            <motion.div
              key={b.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 rounded-2xl border border-white/10 bg-slate-800/60 px-4 py-3"
            >
              <div className="w-32 text-xs text-slate-300">
                <div className="font-semibold text-white">
                  {fmtTimeHM(b.startTime)}–{fmtTimeHM(b.endTime)}
                </div>
                <div>{fmtDateStr(b.date)}</div>
              </div>

              <div className="flex-1">
                <div className="text-sm font-semibold">{b.bookedBy?.name || "Booking"}</div>
                <div className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400/80" />
                  <span>
                    {b.spaceName ||
                      spaces.find((s) => s.id === b.spaceId)?.name ||
                      ""}
                  </span>
                </div>
              </div>

              <button
                onClick={() => handleCancel(b.id)}
                className="rounded-lg bg-slate-700/80 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-white hover:bg-slate-600/80"
              >
                Cancel
              </button>
            </motion.div>
          ))
        )}
      </div>

      {open && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/80 flex items-center justify-center"><div className="text-white">Loading...</div></div>}>
          <SpaceBookingModal
            isOpen={open}
            onClose={() => {
              setOpen(false);
              setSelectedSpace(null);
            }}
            defaultManageTab={manageTab}
            space={selectedSpace}
          />
        </Suspense>
      )}
    </div>
  );
}
